<script setup lang="ts">
import PipedriveUserPicture from '@components/PipedriveUserPicture.vue';
import Icon from '@components/Icon.vue';
import Typography from '@components/Typography.vue';
import { useAppDataStore } from '@stores/useAppDataStore.ts';
import { storeToRefs } from 'pinia';
import { useScreensizeStore } from '@stores/useScreensizeStore';
import type { PipedriveUser } from '@models/groups/selectionItem';
import { flashBagSuccess } from '@tools/flashbag';
import { trans } from '@elements/translation';
import { computed } from 'vue';
import { PhoneNumber } from '@tools/phoneNumber';
import IconCopy from '@icons/ph_photocopier.svg';
import IconPhoneFill from '@icons/ph_phone-fill.svg';

const props = defineProps<{ accountManager: PipedriveUser;}>();

const emit = defineEmits([ 'openModal' ]);

const { isAccountManagerOnline } = storeToRefs(useAppDataStore());

const { isMobileOrTablet } = storeToRefs(useScreensizeStore());

const phoneNumber = computed<PhoneNumber | null>(() => (
  props.accountManager.phone_number ? new PhoneNumber(props.accountManager.phone_number) : null
));

const copyEmail = async (): Promise<void> => {
  await navigator.clipboard.writeText(props.accountManager.email);
  flashBagSuccess(trans('selection_item.contact.email.copied'));
};

</script>

<template>
  <div
    :class="[
      'flex items-center gap-1 rounded-full',
      'border border-solid border-gray',
      'bg-white p-1',
    ]"
  >
    <div class="relative shrink-0">
      <PipedriveUserPicture :pipedrive-user="accountManager" :size="32" :is-online="isAccountManagerOnline" />
    </div>

    <template v-if="isMobileOrTablet">
      <div
        class="inline-flex cursor-pointer pr-1"
        @click="emit('openModal')"
      >
        <Icon :svg="IconPhoneFill" height="24" width="24" fill="gray-darker" />
      </div>
    </template>

    <template v-else>
      <div
        :class="[
          'inline-flex items-center',
          'group',
          'gap-1 text-left'
        ]"
        @click="copyEmail"
      >
        <Typography
          as="span"
          variant="body"
          color="gray-darker"
          :class="[
            'underline decoration-1 underline-offset-[3px]',
            'cursor-pointer'
          ]"
        >
          {{ accountManager.email }}
        </Typography>
        <Icon
          :svg="IconCopy"
          height="16"
          width="16"
          color="gray-darker"
          class="cursor-pointer"
        />
      </div>

      <span
        :class="[
          'mx-1 h-5 w-px',
          'bg-gray-light'
        ]"
        aria-hidden="true"
      />

      <a
        v-if="phoneNumber"
        :href="`tel:${phoneNumber.international}`"
        class="no-underline"
      >
        <Typography
          variant="body-bold"
          color="gray-darker"
          :class="[
            'rounded-[48px_100px_100px_48px]',
            'px-1',
            'hover:bg-blue-lightest hover:text-black',
          ]"
        >
          {{ phoneNumber.printFormattedNational }}
        </Typography>
      </a>
    </template>
  </div>
</template>
